/**
 * Custom cart drawer schema contract (layout family: cart-drawer-v1).
 * Shared by editor, preview, validation scripts, and server tests.
 */
import { POPUP_LAYOUT_IDS } from "./popup-layouts/registry.js";

export const CUSTOM_CART_TEMPLATE_TYPE = "Custom Cart";

export const STATIC_CART_SECTION_INDEX = Object.freeze({
  HEADER_TITLE: 0,
  HEADER_CLOSE: 1,
  HEADER_DIVIDER: 2,
  ANNOUNCEMENT_BAR: 3,
  REWARD_BAR: 4,
  BODY: 5,
  UPSELL_HEADING: 6,
  UPSELL_PRODUCT: 7,
  DISCOUNT_CODE_INPUT: 8,
  ORDER_NOTE: 9,
  FOOTER_DISCOUNT: 10,
  FOOTER_SUBTOTAL_LABEL: 11,
  FOOTER_SUBTOTAL_PRICE: 12,
  FOOTER_TAX_NOTE: 13,
  FOOTER_CHECKOUT: 14,
});

export const STATIC_CART_SECTION_COUNT = 15;

/** Expected section.type per fixed slot (editor + preview indices). */
export const STATIC_CART_SECTION_TYPES = Object.freeze([
  "TITLE",
  "BUTTON",
  "DIVIDER",
  "TITLE",
  "REWARD_BAR",
  "CARTITEMBLOCK",
  "TITLE",
  "PRODUCT",
  "CART_DISCOUNT_INPUT",
  "CART_ORDER_NOTE",
  "TITLE",
  "TITLE",
  "TITLE",
  "TITLE",
  "BUTTON",
]);

const CLOSE_ACTION = "close_popup";
const CHECKOUT_ACTION = "checkout";
const GO_TO_STEP_ACTION = "go_to_step";

export function isStaticCartDrawerContentStep(step) {
  if (!step || typeof step !== "object") return false;
  if (step.cartDrawerLayout?.header && step.cartDrawerLayout?.body) {
    return true;
  }
  const sections = step.section ?? step.sections ?? [];
  return sections.some((s) => s?.type === "CARTITEMBLOCK");
}

export function isStaticCartDrawerTriggerStep(step) {
  if (!step || typeof step !== "object") return false;
  if (isStaticCartDrawerContentStep(step)) return false;
  const sections = (step.section ?? step.sections ?? []).filter((s) => s?.type);
  if (!sections.length) return false;
  return sections.every((s) => s?.type === "BUTTON");
}

function validateDrawerSections(sections, errors, prefix) {
  if (!Array.isArray(sections)) {
    errors.push(`${prefix}: section must be an array`);
    return;
  }
  if (sections.length < STATIC_CART_SECTION_COUNT) {
    errors.push(
      `${prefix}: expected ${STATIC_CART_SECTION_COUNT} sections, got ${sections.length}`
    );
    return;
  }

  STATIC_CART_SECTION_TYPES.forEach((expectedType, index) => {
    const section = sections[index];
    if (!section || section.type !== expectedType) {
      errors.push(
        `${prefix}: slot ${index} expected type ${expectedType}, got ${section?.type ?? "missing"}`
      );
    }
  });

  const title = sections[STATIC_CART_SECTION_INDEX.HEADER_TITLE];
  const closeBtn = sections[STATIC_CART_SECTION_INDEX.HEADER_CLOSE];
  const checkoutBtn = sections[STATIC_CART_SECTION_INDEX.FOOTER_CHECKOUT];

  if (closeBtn?.setting?.action?.type !== CLOSE_ACTION) {
    errors.push(`${prefix}: header close button must use action ${CLOSE_ACTION}`);
  }
  if (checkoutBtn?.setting?.action?.type !== CHECKOUT_ACTION) {
    errors.push(`${prefix}: footer checkout must use action ${CHECKOUT_ACTION}`);
  }
  if (
    sections[STATIC_CART_SECTION_INDEX.FOOTER_SUBTOTAL_PRICE]?.setting
      ?.cartDrawerPart !== "subtotal_price"
  ) {
    errors.push(`${prefix}: footer price slot must set cartDrawerPart subtotal_price`);
  }
}

function stepHasCartDrawerLayout(step) {
  const layout = step?.cartDrawerLayout;
  return Boolean(layout?.header && layout?.body && layout?.footer);
}

/**
 * Validate popup payload for static custom cart drawer.
 * @param {object} popup
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateStaticCartDrawerPopup(popup) {
  const errors = [];

  if (!popup || typeof popup !== "object") {
    return { ok: false, errors: ["popup is required"] };
  }

  const gs = popup.generalSettings || {};
  if (gs.isStaticCartDrawer !== true) {
    errors.push("generalSettings.isStaticCartDrawer must be true");
  }
  if (gs.isDraggable !== false) {
    errors.push("generalSettings.isDraggable must be false");
  }
  const layoutId = gs.popupLayoutId;
  if (layoutId && layoutId !== POPUP_LAYOUT_IDS.CART_DRAWER) {
    errors.push(
      `generalSettings.popupLayoutId must be ${POPUP_LAYOUT_IDS.CART_DRAWER} (got ${layoutId})`
    );
  }

  const popUpType = popup.popUpType || popup.template_type;
  if (popUpType && popUpType !== CUSTOM_CART_TEMPLATE_TYPE) {
    errors.push(`popUpType must be ${CUSTOM_CART_TEMPLATE_TYPE}`);
  }

  const schema = popup.schema;
  if (!Array.isArray(schema) || schema.length === 0) {
    errors.push("schema must be a non-empty array");
    return { ok: false, errors };
  }

  let triggerIndex = null;
  let drawerIndex = null;

  schema.forEach((step, index) => {
    if (isStaticCartDrawerContentStep(step) && drawerIndex == null) {
      drawerIndex = index;
    } else if (isStaticCartDrawerTriggerStep(step) && triggerIndex == null) {
      triggerIndex = index;
    }
  });

  schema.forEach((step, index) => {
    if (isStaticCartDrawerContentStep(step)) {
      const sections = step.section ?? step.sections ?? [];
      validateDrawerSections(sections, errors, `schema[${index}]`);
      if (!stepHasCartDrawerLayout(step)) {
        errors.push(`schema[${index}]: cartDrawerLayout.header/body/footer required`);
      }
    } else if (isStaticCartDrawerTriggerStep(step)) {
      const btn = (step.section ?? step.sections ?? [])[0];
      if (btn?.setting?.action?.type !== GO_TO_STEP_ACTION) {
        errors.push(`schema[${index}]: trigger button must use action ${GO_TO_STEP_ACTION}`);
      } else if (drawerIndex != null) {
        const openStep = btn.setting.action.parameters?.openStep;
        if (openStep !== drawerIndex) {
          errors.push(
            `schema[${index}]: trigger openStep must be ${drawerIndex} (got ${openStep})`
          );
        }
      }
    }
  });

  if (drawerIndex == null) {
    errors.push("schema must include one drawer step (CARTITEMBLOCK)");
  }

  return { ok: errors.length === 0, errors };
}
